import { useEffect, useState } from 'react';
import { financialOverviewApi, type FinancialOverview } from './financial-overview-api';
import { remunerationMoney as money } from './RemunerationColumn';
import { PrototypeIcon } from './PrototypeIcon';
import { FinanceExplanation } from './FinanceExplanation';
import { FinancialAmount, FinancialCalculationStatus } from './FinancialCalculationStatus';
import { ProvisionsSummary } from './ProvisionsSummary';
import type { FinancialCalculationKey } from './financial-calculation-roadmap';

const categoryKey = (row: { sourceConnectionId: string; categoryId: string }): string =>
  JSON.stringify([row.sourceConnectionId, row.categoryId]);
const normalize = (name: string): string =>
  name
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .toLowerCase();

/** Les paiements déjà pointés sont disponibles même sans dette rapprochée.
 * Les garder séparés du PDF interdit de retrancher un règlement deux fois. */
export function ProvisionsSourcesCard({
  overview,
}: {
  readonly overview: FinancialOverview;
}): React.JSX.Element {
  const [period, setPeriod] = useState(overview.period);
  const [loaded, setLoaded] = useState<FinancialOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLoaded(null);
    setError(null);
    if (period === overview.period) return;
    let active = true;
    void financialOverviewApi.read(period).then(
      (value) => {
        if (active) setLoaded(value);
      },
      () => {
        if (active) setError('Provisions indisponibles pour ce mois.');
      },
    );
    return () => {
      active = false;
    };
  }, [overview, period]);
  const source = period === overview.period ? overview : loaded?.period === period ? loaded : null;
  const month = source?.remuneration?.months.find((row) => row.period === period);
  const payments = month?.expenses ?? [];
  const paid = (accepts: (name: string) => boolean): string | null => {
    const matching = payments.filter((row) => accepts(normalize(row.name)));
    return matching.length || month?.expensesComplete === true
      ? matching.reduce((sum, row) => sum + BigInt(row.paidCents), 0n).toString()
      : null;
  };
  const fixedKeys = new Set(
    source?.expenseCategoryKinds?.filter((row) => row.kind === 'fixed').map(categoryKey),
  );
  const fixedPayments = payments.filter((row) => fixedKeys.has(categoryKey(row)));
  const fixedPaid =
    fixedKeys.size === 0 || (fixedPayments.length === 0 && month?.expensesComplete !== true)
      ? null
      : fixedPayments.reduce((sum, row) => sum + BigInt(row.paidCents), 0n).toString();
  const quarter = `${period.slice(0, 4)}-Q${Math.ceil(Number(period.slice(5)) / 3)}`;
  const vat = source?.vatDeclarations
    ?.filter((entry) => entry.period <= quarter)
    .sort((a, b) => b.period.localeCompare(a.period))[0];
  const rows = [
    {
      calculation: 'urssaf' as FinancialCalculationKey,
      name: `URSSAF · PDF ${source?.remuneration?.provisionPeriod ?? 'non disponible'}`,
      due: source?.remuneration?.provisionDueCents ?? null,
      paid: paid((name) => /^cotisations? sociales?$/u.test(name)),
      remaining:
        source?.remuneration?.urssafPaymentBalance?.remainingCents ??
        source?.remuneration?.urssafSourcePayment?.remainingCents ??
        null,
      paymentSource:
        source?.remuneration?.urssafPaymentBalance?.remainingCents != null
          ? 'Débits attribués au PDF'
          : source?.remuneration?.urssafSourcePayment?.status === 'reported_paid'
            ? `Réglé dans Freebe le ${source.remuneration.urssafSourcePayment.paymentDate}`
            : null,
    },
    ...(source?.remuneration?.socialEstimate
      ? [
          {
            calculation: 'socialEstimate' as FinancialCalculationKey,
            name: `URSSAF / versement libératoire · estimation ${period}`,
            due: source.remuneration.socialEstimate.dueCents,
            paid: null,
            remaining: null,
            paymentSource: 'Sur le CA HT déjà encaissé ; paiement non établi',
          },
        ]
      : []),
    {
      calculation: 'vat' as FinancialCalculationKey,
      name: source?.revenueSummary
        ? `TVA collectée · ${source.revenueSummary.quarter.from} à ${source.revenueSummary.quarter.through}`
        : 'TVA collectée · trimestre',
      due:
        source?.revenueSummary?.quarter.complete === true
          ? source.revenueSummary.quarter.vatCents
          : null,
      paid: paid((name) => /^(?:debit de )?tva$/u.test(name)),
    },
    ...(vat
      ? [
          {
            calculation: 'vat' as FinancialCalculationKey,
            name: `TVA nette · CA3 ${vat.period}`,
            due: vat.netDueCents,
            paid: null,
            remaining: vat.netDueCents === '0' ? '0' : null,
            paymentSource:
              vat.status === 'conflict'
                ? 'Versions du même dépôt contradictoires · S5'
                : `Déposée le ${vat.filedOn.split('-').reverse().join('/')} · ${vat.creditToCarryCents !== '0' ? `crédit à reporter : ${money(vat.creditToCarryCents)}` : 'paiement à établir séparément'}`,
          },
        ]
      : []),
    {
      calculation: 'scheduledExpenses' as FinancialCalculationKey,
      name: 'Dépenses qualifiées fixes · paiements du mois',
      due: null,
      paid: fixedPaid,
    },
  ];
  return (
    <article className="provision-proposal finance-card--controls">
      <header className="finance-card__heading">
        <h3 className="finance-card__title">
          <PrototypeIcon name="wallet" />À garder de côté
        </h3>
        <div className="finance-card__controls">
          <select
            aria-label="Mois des provisions proposées"
            value={period}
            onChange={(event) => setPeriod(event.currentTarget.value)}
          >
            {(overview.remuneration?.months ?? [])
              .slice()
              .reverse()
              .map((row) => (
                <option key={row.period} value={row.period}>
                  {new Intl.DateTimeFormat('fr-FR', {
                    month: 'long',
                    year: 'numeric',
                    timeZone: 'UTC',
                  }).format(new Date(`${row.period}-01T12:00:00Z`))}
                </option>
              ))}
          </select>
        </div>
      </header>
      {error ? (
        <p className="tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : source === null ? (
        <p role="status">Chargement des provisions…</p>
      ) : (
        <>
          {source.obligations ? (
            <ProvisionsSummary source={source} />
          ) : (
            <>
              <div className="provision-proposal__total">
                <span>À provisionner</span>
                <FinancialCalculationStatus calculation="provisions" />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Pour quoi ?</th>
                    <th>Déclaré</th>
                    <th>Payé dans le mois</th>
                    <th>Reste à payer</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.name}>
                      <th scope="row">{row.name}</th>
                      <td>
                        <FinancialAmount cents={row.due} calculation={row.calculation} />
                      </td>
                      <td>
                        <FinancialAmount cents={row.paid} calculation={row.calculation} />
                      </td>
                      <td>
                        {row.remaining != null ? money(row.remaining) : 'Non établi'}
                        {row.paymentSource ? <small>{row.paymentSource}</small> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="provision-proposal__reserve">
                <span>Réserve de sécurité</span>
                <b>{money(source.reserveTargetCents)}</b>
              </p>
              <FinanceExplanation title="Voir le détail">
                <p>
                  Déclarations et paiements sont distincts. Un paiement du mois ne solde pas
                  automatiquement une autre période.
                </p>
                {source.remuneration?.urssafPaymentBalance ? (
                  <p>
                    PDF · paiements attribués constatés au{' '}
                    {source.remuneration.urssafPaymentBalance.asOf} :{' '}
                    {money(source.remuneration.urssafPaymentBalance.attributedPaidCents)}.
                  </p>
                ) : null}
                {source.remuneration?.provisionComponents ? (
                  <p>
                    Ventilation du PDF {source.remuneration.provisionPeriod} : cotisations{' '}
                    {money(source.remuneration.provisionComponents.socialCents)}, formation{' '}
                    {money(source.remuneration.provisionComponents.trainingCents)}, versement
                    libératoire {money(source.remuneration.provisionComponents.incomeTaxCents)} ;
                    déjà inclus dans le total.
                  </p>
                ) : null}
                {source.remuneration?.socialEstimate ? (
                  <p>
                    URSSAF / impôt libératoire estimés pour {period} :{' '}
                    {money(source.remuneration.socialEstimate.dueCents)}, sur le CA HT encaissé, aux
                    taux du PDF {source.remuneration.socialEstimate.sourcePeriod}.
                  </p>
                ) : null}
                {source.revenueSummary ? (
                  <p>
                    TVA sur les recettes encaissées du trimestre :{' '}
                    {money(source.revenueSummary.quarter.vatCents)}. Ce montant n’est pas la TVA
                    nette à payer.
                  </p>
                ) : null}
                {source.purchasePaymentPosition ? (
                  <div className="provision-proposal__purchases">
                    <b>
                      Achats restant à régler · sous-total connu :{' '}
                      {money(source.purchasePaymentPosition.knownRemainingCents)}
                    </b>
                    <p>
                      {source.purchasePaymentPosition.reason}{' '}
                      {source.purchasePaymentPosition.laterSnapshots} photographie(s)
                      postérieure(s).
                    </p>
                  </div>
                ) : null}
              </FinanceExplanation>
            </>
          )}
        </>
      )}
    </article>
  );
}

/** Même identité Freebe et même année de paiement. Ne pas classer des clients
 * par « bénéfice » tant que les coûts communs et directs ne sont pas rattachés. */
export function ClientRevenueSourcesCard({
  overview,
}: {
  readonly overview: FinancialOverview;
}): React.JSX.Element {
  const latestYear = Number((overview.observedThroughPeriod ?? overview.period).slice(0, 4));
  const [year, setYear] = useState(overview.period.slice(0, 4));
  const [scope, setScope] = useState('clients');
  const groups = new Map<string, { name: string; cents: bigint | null; complete: boolean }>();
  for (const month of overview.remuneration?.months ?? []) {
    if (!month.period.startsWith(`${year}-`)) continue;
    for (const client of month.clientRevenues ?? []) {
      const key = JSON.stringify([client.sourceConnectionId, client.sourceClientId]);
      const previous = groups.get(key);
      groups.set(key, {
        name: client.name,
        cents:
          client.receivedHtCents === null
            ? (previous?.cents ?? null)
            : (previous?.cents ?? 0n) + BigInt(client.receivedHtCents),
        complete: (previous?.complete ?? true) && client.complete,
      });
    }
  }
  const allocation = overview.clientCostAllocations?.find((entry) => entry.year === Number(year));
  const costs = new Map(
    allocation?.rows.map((row) => [
      JSON.stringify([row.sourceConnectionId, row.sourceClientId]),
      row,
    ]),
  );
  const rows = [...groups.entries()].sort(([aKey, a], [bKey, b]) => {
    const aMargin = costs.get(aKey)?.marginOnKnownCostsCents;
    const bMargin = costs.get(bKey)?.marginOnKnownCostsCents;
    if (aMargin != null && bMargin == null) return -1;
    if (bMargin != null && aMargin == null) return 1;
    const left = aMargin == null ? a.cents : BigInt(aMargin);
    const right = bMargin == null ? b.cents : BigInt(bMargin);
    if (left === null && right !== null) return 1;
    if (right === null && left !== null) return -1;
    return left !== null && right !== null && left !== right
      ? left > right
        ? -1
        : 1
      : a.name.localeCompare(b.name, 'fr') || aKey.localeCompare(bKey);
  });
  return (
    <article className="profitability-proposal finance-card--controls">
      <header className="finance-card__heading">
        <h3 className="finance-card__title">
          <PrototypeIcon name="piggy-bank" />
          Ce qui vous rapporte
        </h3>
        <div className="finance-card__controls">
          <select
            aria-label="Année de rentabilité proposée"
            value={year}
            onChange={(event) => setYear(event.currentTarget.value)}
          >
            {Array.from({ length: latestYear - 2019 }, (_, i) => latestYear - i).map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </div>
      </header>
      <div
        className="profitability-proposal__scope"
        role="group"
        aria-label="Comparer les clients ou les projets"
      >
        {['clients', 'projets'].map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={scope === value}
            onClick={() => setScope(value)}
          >
            {value === 'clients' ? 'Clients' : 'Projets'}
          </button>
        ))}
      </div>
      {scope === 'clients' && allocation && !allocation.complete ? (
        <p className="profitability-proposal__disclaimer">
          Marges partielles · coûts communs connus, hors coûts directs et temps.
        </p>
      ) : null}
      {scope === 'projets' ? (
        <p>
          <FinancialCalculationStatus calculation="projectMargin" label="Projets non raccordés" />{' '}
          Les coûts du temps réel seront enrichis en S10-03.
        </p>
      ) : rows.length === 0 ? (
        <p>Aucune recette client rattachée dans les opérations disponibles de cette année.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Client Freebe</th>
              <th>Recettes HT connues</th>
              <th>Coûts communs HT imputés</th>
              <th>Marge sur coûts connus</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, row]) => {
              const cost = costs.get(key);
              return (
                <tr key={key}>
                  <th scope="row">{row.name}</th>
                  <td>
                    <FinancialAmount cents={row.cents?.toString()} />
                    {!row.complete ? <small>Sous-total</small> : null}
                  </td>
                  <td>
                    <FinancialAmount
                      cents={cost?.allocatedKnownCostsCents}
                      calculation="clientCosts"
                      reason="Base de coûts HT payés ou de recettes positives insuffisante pour une imputation."
                    />
                  </td>
                  <td>
                    <FinancialAmount
                      cents={cost?.marginOnKnownCostsCents}
                      calculation="clientMargin"
                      reason="Coûts connus ou recettes HT non valorisés."
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <FinanceExplanation>
        {allocation ? (
          <>
            <p>{allocation.reason}</p>
            {allocation.unresolved.length ? <p>{allocation.unresolved.join(' · ')}</p> : null}
          </>
        ) : null}
        <p>
          Recettes HT encaissées, nettes des remboursements et rattachées par identifiant client
          Freebe. Les coûts communs connus sont imputés au prorata des recettes positives. Un coût
          absent reste signalé.
        </p>
        <p>
          Marges sur coûts connus par ordre décroissant ; marges indisponibles en fin de liste,
          classées par recettes connues. Ce classement reste partiel.
        </p>
        <p>
          Coûts communs Client calculés dans S5 ; coûts rattachés aux Projets : S7-05 ; temps réels
          et taux validés : S10-03.
        </p>
      </FinanceExplanation>
    </article>
  );
}
